import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Descriptions, Empty, Flex, Form, Input, InputNumber, Result, Row, Select, Skeleton, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle, usePoll } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { PlatformLabel } from "../channels/PlatformBadge";
import { CaseKindTag, CaseStatusTag, DetailsFields, GradeTag, KINDS, RESOLUTIONS, RespondDeadline, type Case, type CaseKind, type DetailsValues, type Resolution } from "./common";
import { AnswerCard, CorrectionCard, EvidenceCard, EvidenceVideo, MarketplaceRecords, PackingEvidence } from "./Evidence";
import { ClaimsCard } from "./Claims";

type Unit = Case["units"][number];
type Line = Case["lines"][number];

/** Detail of an aftersales case: what the buyer asked, what CS expects, what the warehouse found, the decision. */
export function CaseDetail() {
  const { caseID = "" } = useParams();
  const { t, formatDateTime, formatMoney } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");
  const path = { params: { path: { caseID } } };
  const q = useQuery({
    queryKey: ["aftersales", "case", caseID],
    queryFn: () => unwrap(api.GET("/v1/aftersales/{caseID}", path)),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
    refetchInterval: usePoll(30_000),
  });
  useTabTitle(q.data?.number);
  const done = (c: Case) => {
    queryClient.setQueryData(["aftersales", "case", caseID], c);
    void queryClient.invalidateQueries({ queryKey: ["aftersales"] });
  };
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/aftersales/{caseID}/cancel", path)),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });

  if (q.isError) {
    return q.error instanceof ApiError && q.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(q.error)} />;
  }
  const c = q.data;
  if (!c) return <Skeleton active />;
  const closed = c.status === "resolved" || c.status === "cancelled";
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {c.number}
          </Typography.Title>
          <CaseStatusTag status={c.status} />
          <CaseKindTag kind={c.kind} />
          {c.respondAction && <RespondDeadline at={c.respondBy} />}
          <Typography.Text type="secondary">
            {t(`as.source.${c.source}`)} · {c.createdName} · {formatDateTime(c.createdAt, tz)}
          </Typography.Text>
        </Space>
        <Space wrap>
          {can("warehouse.operate") && (c.status === "open" || c.status === "receiving") && c.kind !== "refund_only" && (
            <Button onClick={() => navigate(`/app/warehouse/returns?case=${c.id}`)}>{t("as.openDock")}</Button>
          )}
          {manage && c.status === "open" && (
            <Button
              danger
              loading={cancel.isPending}
              onClick={() =>
                modal.confirm({
                  title: t("as.cancel"),
                  content: t("as.cancelConfirm", { number: c.number }),
                  okButtonProps: { danger: true },
                  okText: t("as.cancel"),
                  cancelText: t("common.close"),
                  onOk: () => cancel.mutateAsync(),
                })
              }
            >
              {t("as.cancel")}
            </Button>
          )}
        </Space>
      </Flex>
      {c.status === "receiving" && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("as.receivingNow", { warehouse: c.warehouseName })} />}
      {c.kind === "unidentified" && !closed && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("as.unidentifiedHelp")} />}
      {c.respondAction && !c.canAnswer && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("as.answer.inSellerCenter")} />}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            {(c.source === "marketplace" || c.buyerReason || c.buyerNote) && (
              <Card size="small" title={t("as.buyer")}>
                <Descriptions column={{ xs: 1, md: 2 }} size="small">
                  <Descriptions.Item label={t("as.buyerReason")}>{c.buyerReason || "—"}</Descriptions.Item>
                  <Descriptions.Item label={t("as.refundRequested")}>{c.refundRequested != null ? formatMoney(c.refundRequested) : "—"}</Descriptions.Item>
                  <Descriptions.Item label={t("as.buyerNote")} span={2}>
                    {c.buyerNote || "—"}
                  </Descriptions.Item>
                  {c.platformStatus && (
                    <Descriptions.Item label={t("as.platformStatus")}>
                      <span className="z-num">{c.platformStatus}</span>
                    </Descriptions.Item>
                  )}
                  {c.externalId && (
                    <Descriptions.Item label={t("as.externalId")}>
                      <span className="z-num">{c.externalId}</span>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
            <PackingEvidence c={c} canCapture={manage && c.status !== "cancelled"} />
            <EvidenceCard c={c} editable={manage && c.status !== "cancelled"} />
            <Card size="small" title={t("as.expected")}>
              <Table<Line>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={c.lines}
                locale={{ emptyText: t("as.noLines") }}
                columns={[
                  {
                    title: t("as.item"),
                    render: (_, l) => (
                      <Space orientation="vertical" size={0}>
                        <Typography.Text strong className="z-num">
                          {l.skuCode || l.sellerSku || "—"}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {l.name}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  { title: t("as.expectedCol"), dataIndex: "expected", width: 110, align: "right" },
                  {
                    title: t("as.receivedCol"),
                    dataIndex: "received",
                    width: 110,
                    align: "right",
                    render: (v: number, l) => (
                      <Typography.Text className="z-num" type={c.receivedAt && v !== l.expected ? "warning" : undefined}>
                        {v}
                      </Typography.Text>
                    ),
                  },
                ]}
              />
            </Card>
            <ResultCard c={c} canCapture={manage && c.status !== "cancelled"} />
            <CorrectionCard c={c} editable={manage} onDone={done} />
            <MarketplaceRecords c={c} />
          </Space>
        </Col>
        <Col xs={24} xl={8}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            {manage && c.canAnswer && c.respondAction && c.status !== "cancelled" && <AnswerCard c={c} onDone={done} />}
            <ClaimsCard c={c} onDone={done} />
            {c.order ? (
              <Card size="small" title={t("as.col.order")}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t("orders.number")}>
                    <Typography.Link className="z-num" onClick={() => navigate(`/app/orders/${c.order!.id}`)}>
                      {c.order.number}
                    </Typography.Link>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("orders.store")}>
                    <Space size={4}>
                      <PlatformLabel platform={c.order.platform as Schemas["ChannelPlatformName"]} />· {c.order.shopName}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("orders.buyer")}>{c.order.buyerName || "—"}</Descriptions.Item>
                  <Descriptions.Item label={t("orders.total")}>{formatMoney(c.order.total)}</Descriptions.Item>
                  <Descriptions.Item label={t("as.outboundTracking")}>{c.order.trackingNo ? <span className="z-num">{c.order.trackingNo}</span> : "—"}</Descriptions.Item>
                </Descriptions>
              </Card>
            ) : (
              c.orderNumber && (
                <Card size="small" title={t("as.col.order")}>
                  <Typography.Text className="z-num">{c.orderNumber}</Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                    {t("as.orderNotInZerpid")}
                  </Typography.Paragraph>
                </Card>
              )
            )}
            {c.kind === "unidentified" && manage && c.status !== "cancelled" && <LinkCard c={c} onDone={done} />}
            <InstructionsCard c={c} editable={manage && !closed} onDone={done} />
            {!(c.canAnswer && c.respondAction) && <DecisionCard c={c} editable={manage} onDone={done} />}
          </Space>
        </Col>
      </Row>
    </>
  );
}

function ResultCard({ c, canCapture }: { c: Case; canCapture: boolean }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const navigate = useNavigate();
  const tz = me?.workspace?.timezone;
  return (
    <Card size="small" title={t("as.result")}>
      {c.units.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("as.noResult")} />
      ) : (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          {c.receivedAt && (
            <Typography.Text type="secondary">
              {t("as.receivedBy", { by: c.receivedName, at: formatDateTime(c.receivedAt, tz), warehouse: c.warehouseName })}
              {c.receivedNote ? ` · ${c.receivedNote}` : ""}
            </Typography.Text>
          )}
          <UnitsTable units={c.units} />
          {c.putawayTaskId && (
            <Button size="small" onClick={() => navigate(`/app/warehouse/tasks/${c.putawayTaskId}`)}>
              {t("wms.in.openPutaway")}
            </Button>
          )}
        </Space>
      )}
      <div style={{ marginTop: 12 }}>
        <CaseVideo c={c} canCapture={canCapture} />
      </div>
    </Card>
  );
}

export function UnitsTable({ units, onRemove }: { units: Unit[]; onRemove?: (u: Unit) => void }) {
  const { t } = useI18n();
  return (
    <Table<Unit>
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={units}
      columns={[
        { title: t("as.dock.grade"), dataIndex: "grade", width: 130, render: (g: Unit["grade"]) => <GradeTag grade={g} /> },
        {
          title: t("as.item"),
          render: (_, u) =>
            u.skuId ? (
              <Space orientation="vertical" size={0}>
                <Typography.Text strong className="z-num">
                  {u.skuCode}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[u.skuName, u.serialNo && t("as.unit.serial", { sn: u.serialNo }), u.lotNo && t("as.unit.lot", { lot: u.lotNo })].filter(Boolean).join(" · ")}
                </Typography.Text>
                {u.serialOverride && (
                  <Tag color="error" style={{ whiteSpace: "normal", marginTop: 2 }}>
                    {t("serial.return.overridden", { reason: u.serialOverride })}
                  </Tag>
                )}
              </Space>
            ) : (
              <Typography.Text italic>{u.description}</Typography.Text>
            ),
        },
        { title: t("as.dock.quantity"), dataIndex: "quantity", width: 80, align: "right" },
        { title: t("as.note"), dataIndex: "note", render: (v: string) => v || "—" },
        ...(onRemove
          ? [
              {
                title: "",
                width: 80,
                render: (_: unknown, u: Unit) => (
                  <Button size="small" type="link" danger onClick={() => onRemove(u)}>
                    {t("common.delete")}
                  </Button>
                ),
              },
            ]
          : [{ title: t("as.by"), dataIndex: "createdName", width: 140 }]),
      ]}
    />
  );
}

/** The unboxing video, fetched on demand (it can be large); frames become evidence. */
function CaseVideo({ c, canCapture }: { c: Case; canCapture: boolean }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  if (!c.video) return <Typography.Text type="secondary">{t("as.noVideo")}</Typography.Text>;
  return (
    <Space orientation="vertical" size={4} style={{ width: "100%" }}>
      <EvidenceVideo
        caseId={c.id}
        source="unboxing_video"
        canCapture={canCapture}
        stamp={`${c.number} · ${t("as.ev.source.unboxing_video")} ${formatDateTime(c.video.recordedAt, tz)}`}
        load={() => unwrap(api.GET("/v1/aftersales/{caseID}/video", { params: { path: { caseID: c.id } }, parseAs: "blob" }))}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t("orders.videoKept", { at: formatDateTime(c.video.expiresAt, tz) })}
      </Typography.Text>
    </Space>
  );
}

function InstructionsCard({ c, editable, onDone }: { c: Case; editable: boolean; onDone: (c: Case) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<DetailsValues>();
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (v: DetailsValues) =>
      unwrap(
        api.PATCH("/v1/aftersales/{caseID}", {
          params: { path: { caseID: c.id } },
          body: {
            kind: (c.kind === "unidentified" ? "return_refund" : v.kind) as CaseKind,
            reason: v.reason ?? "",
            instructions: v.instructions ?? "",
            checks: (v.checks ?? []) as ("seal" | "serial" | "complete" | "function")[],
            returnTrackingNo: v.returnTrackingNo ?? "",
            returnCarrier: v.returnCarrier ?? "",
            warehouseId: v.warehouseId ?? null,
          },
        }),
      ),
    onSuccess: (x) => {
      onDone(x);
      setEditing(false);
      void message.success(t("as.saved"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const edit = () => {
    form.setFieldsValue({
      kind: c.kind === "unidentified" ? "return_refund" : (c.kind as CaseKind),
      reason: c.reason || undefined,
      instructions: c.instructions,
      checks: c.checks,
      returnTrackingNo: c.returnTrackingNo,
      returnCarrier: c.returnCarrier,
      warehouseId: c.warehouseId,
    });
    setEditing(true);
  };
  return (
    <Card
      size="small"
      title={t("as.cs")}
      extra={
        editable &&
        !editing && (
          <Button size="small" onClick={edit}>
            {t("common.edit")}
          </Button>
        )
      }
    >
      {editing ? (
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)} requiredMark={false}>
          <DetailsFields kindLocked={c.kind === "unidentified"} warehouseLocked={c.status !== "open"} />
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t("common.save")}
            </Button>
            <Button onClick={() => setEditing(false)}>{t("common.cancel")}</Button>
          </Space>
        </Form>
      ) : (
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t("as.reasonLabel")}>{c.reason ? t(`as.reason.${c.reason}`) : t("as.reason.none")}</Descriptions.Item>
          <Descriptions.Item label={t("as.instructions")}>
            <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{c.instructions || "—"}</Typography.Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label={t("as.checks")}>{c.checks.length ? c.checks.map((x) => t(`as.check.${x}`)).join(", ") : "—"}</Descriptions.Item>
          <Descriptions.Item label={t("as.returnTracking")}>
            {c.returnTrackingNo ? (
              <span className="z-num">
                {c.returnTrackingNo}
                {c.returnCarrier ? ` · ${c.returnCarrier}` : ""}
              </span>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("as.warehouse")}>{c.warehouseName || "—"}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

type DecisionValues = { resolution: Resolution; refundAmount?: number; note?: string };

function DecisionCard({ c, editable, onDone }: { c: Case; editable: boolean; onDone: (c: Case) => void }) {
  const { t, formatDateTime, formatMoney } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const tz = me?.workspace?.timezone;
  const [form] = Form.useForm<DecisionValues>();
  const resolution = Form.useWatch("resolution", form);
  const resolve = useMutation({
    mutationFn: (v: DecisionValues) =>
      unwrap(
        api.POST("/v1/aftersales/{caseID}/resolve", {
          params: { path: { caseID: c.id } },
          body: { resolution: v.resolution, refundAmount: v.refundAmount ?? null, note: v.note ?? "" },
        }),
      ),
    onSuccess: (x) => {
      onDone(x);
      void message.success(t("as.resolvedMsg"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  if (c.status === "resolved" && c.resolution) {
    return (
      <Card size="small" title={t("as.decision")}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t("as.decision")}>{t(`as.resolution.${c.resolution}`)}</Descriptions.Item>
          {c.refundAmount != null && <Descriptions.Item label={t("as.refundAmount")}>{formatMoney(c.refundAmount)}</Descriptions.Item>}
          {c.resolutionNote && <Descriptions.Item label={t("as.note")}>{c.resolutionNote}</Descriptions.Item>}
          <Descriptions.Item label={t("as.by")}>
            {c.resolvedName} · {c.resolvedAt ? formatDateTime(c.resolvedAt, tz) : ""}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }
  if (c.status === "cancelled" || !editable) return null;
  const blocked = c.status === "receiving" ? t("as.waitReceiving") : c.kind === "unidentified" ? t("as.linkFirst") : null;
  const goodsPending = c.status === "open" && c.lines.some((l) => l.expected > 0) && c.kind !== "refund_only" && c.kind !== "complaint";
  return (
    <Card size="small" title={t("as.decision")}>
      {blocked ? (
        <Typography.Text type="secondary">{blocked}</Typography.Text>
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => resolve.mutate(v)} requiredMark={false}>
          {goodsPending && <Alert type="warning" showIcon style={{ marginBottom: 12 }} title={t("as.goodsPending")} />}
          <Form.Item name="resolution" label={t("as.decision")} rules={[{ required: true }]}>
            <Select options={RESOLUTIONS.map((r) => ({ value: r, label: t(`as.resolution.${r}`) }))} />
          </Form.Item>
          {(resolution === "refund" || resolution === "partial_refund") && (
            <Form.Item name="refundAmount" label={t("as.refundAmount")} rules={[{ required: resolution === "partial_refund" }]}>
              <InputNumber min={0} precision={0} prefix="Rp" style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item name="note" label={t("as.note")}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={resolve.isPending}>
            {t("as.resolve")}
          </Button>
        </Form>
      )}
    </Card>
  );
}

/** CS matches an unidentified parcel to its order. */
function LinkCard({ c, onDone }: { c: Case; onDone: (c: Case) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [form] = Form.useForm<{ number: string; kind: CaseKind }>();
  const link = useMutation({
    mutationFn: async (v: { number: string; kind: CaseKind }) => {
      const list = await unwrap(api.GET("/v1/orders", { params: { query: { q: v.number.trim() } } }));
      const hit = list.items.find((o) => o.externalId.toUpperCase() === v.number.trim().toUpperCase());
      if (!hit) return null;
      return unwrap(api.POST("/v1/aftersales/{caseID}/link", { params: { path: { caseID: c.id } }, body: { orderId: hit.id, kind: v.kind } }));
    },
    onSuccess: (x) => {
      if (!x) {
        form.setFields([{ name: "number", errors: [t("as.orderNotFound")] }]);
        return;
      }
      onDone(x);
      void message.success(t("as.linked"));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Card size="small" title={t("as.link")}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t("as.linkHelp")}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" initialValues={{ kind: "return_refund" }} onFinish={(v) => link.mutate(v)} requiredMark={false}>
        <Form.Item name="number" label={t("as.findOrder")} rules={[{ required: true, whitespace: true }]}>
          <Input className="z-num" />
        </Form.Item>
        <Form.Item name="kind" label={t("as.kindLabel")}>
          <Select options={KINDS.map((k) => ({ value: k, label: t(`as.kind.${k}`) }))} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={link.isPending}>
          {t("as.link")}
        </Button>
      </Form>
    </Card>
  );
}
